import React, { useState, useEffect } from 'react';
import { discountAPI, doctorAPI } from '../services/api';
import { PageHeader, ActionBtn, Modal, FormField, Input, Select, Textarea } from '../components/Shared';
import toast from 'react-hot-toast';

// @discount-codes-portal-v1
// Goodwill / discount codes. Rule enforced by the backend: a code never
// costs a doctor money. "Έκπτωση" comes out of DocNow's commission;
// "Δωρεάν" only works with doctors who have agreed to waive their fee.

const KIND_EL = { platform: 'Έκπτωση', waiver: 'Δωρεάν' };
const KIND_COLOR = { platform: '#2563EB', waiver: '#0D9A6A' };

const Pill = ({ text, color }) => (
  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11,
                 fontWeight: 700, color, background: color + '18', whiteSpace: 'nowrap' }}>{text}</span>
);
const euros = (c) => (Number(c || 0) / 100).toFixed(2) + ' €';
const fmtDate = (d) => d ? new Date(d).toLocaleString('el-GR', { timeZone: 'Europe/Athens' }) : '—';

const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280', fontWeight: 700, borderBottom: '1px solid #e5e7eb' };
const td = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

const EMPTY_FORM = { kind: 'platform', unit: 'eur', discountEuros: '', discountPercent: '', doctorId: '', patientEmail: '', maxUses: 1, expiresAt: '', note: '', code: '' };

export default function DiscountCodesPage() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [waiverDoctors, setWaiverDoctors] = useState([]);
  const [allDoctors, setAllDoctors] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [selected, setSelected] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [redLoading, setRedLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await discountAPI.list();
      setCodes(data.data || []);
    } catch (e) { toast.error('Αδυναμία φόρτωσης κωδικών'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    discountAPI.waiverDoctors()
      .then(({ data }) => setWaiverDoctors(data.data || []))
      .catch(() => setWaiverDoctors([]));
    // @discount-percent-v1
    // Every doctor, for the optional restriction on a platform code -
    // the field used to ask for a raw uuid. And the live price points,
    // so the form can say what the code is really worth. Both degrade
    // to an empty list rather than blocking the form.
    doctorAPI.getAll()
      .then(({ data }) => setAllDoctors(
        (data.data || [])
          .filter((d) => !d.isLeaver)
          .sort((a, b) => String(a.last_name || '').localeCompare(String(b.last_name || ''), 'el'))
      ))
      .catch(() => setAllDoctors([]));
    discountAPI.pricingImpact()
      .then(({ data }) => setPricing(data.data || []))
      .catch(() => setPricing([]));
  }, []);

  const openRedemptions = async (row) => {
    setSelected(row); setRedLoading(true);
    try {
      const { data } = await discountAPI.redemptions(row.id);
      setRedemptions(data.data || []);
    } catch (e) { toast.error('Αδυναμία φόρτωσης χρήσεων'); setRedemptions([]); }
    finally { setRedLoading(false); }
  };

  const toggleActive = async (row) => {
    try {
      await discountAPI.setActive(row.id, !row.is_active);
      toast.success(row.is_active ? 'Ο κωδικός απενεργοποιήθηκε' : 'Ο κωδικός ενεργοποιήθηκε');
      load();
    } catch (e) { toast.error('Η αλλαγή απέτυχε'); }
  };

  // @discount-percent-v1
  // What the code will really be worth, at every price point that is
  // live today. The ceiling is the same one discount.service.js applies
  // at redemption - a platform code cannot eat past DocNow's commission
  // and a waiver cannot pass the fee - reproduced here for one reason:
  // so the number is visible while the code is being written, instead
  // of being discovered later in the takings.
  const impact = React.useMemo(() => {
    const pct = form.unit === 'pct' ? Number(form.discountPercent) : null;
    const eur = form.unit === 'eur' ? Number(form.discountEuros) : null;
    const usable = form.unit === 'pct' ? (pct > 0 && pct <= 100) : (eur > 0);
    if (!usable) return [];
    return pricing.map((p) => {
      const fee = Number(p.fee_cents);
      const requested = pct != null ? Math.round(fee * pct / 100) : Math.round(eur * 100);
      const ceiling = form.kind === 'waiver' ? fee : Number(p.commission_cents);
      const actual = Math.min(requested, ceiling);
      return {
        key: p.service_name + ':' + fee + ':' + p.commission_cents,
        name: p.service_name,
        fee,
        actual,
        requested,
        clipped: requested > ceiling,
        share: fee > 0 ? (actual / fee) * 100 : 0
      };
    });
  }, [pricing, form.unit, form.discountEuros, form.discountPercent, form.kind]);

  const submit = async () => {
    if (form.unit === 'pct') {
      const p = Number(form.discountPercent);
      if (!(p > 0 && p <= 100)) return toast.error('Δώστε ποσοστό από 1 έως 100');
    } else if (!(Number(form.discountEuros) > 0)) {
      return toast.error('Δώστε ποσό μεγαλύτερο από 0');
    }
    if (form.kind === 'waiver' && !form.doctorId) return toast.error('Οι δωρεάν κωδικοί απαιτούν ιατρό');
    setSaving(true);
    try {
      const payload = {
        kind: form.kind,
        discountEuros: form.unit === 'eur' ? Number(form.discountEuros) : undefined,
        discountPercent: form.unit === 'pct' ? Number(form.discountPercent) : undefined,
        doctorId: form.doctorId || undefined,
        patientEmail: form.patientEmail.trim() || undefined,
        maxUses: Number(form.maxUses) || 1,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : undefined,
        note: form.note.trim() || undefined,
        code: form.code.trim() || undefined,
      };
      const { data } = await discountAPI.create(payload);
      toast.success(`Δημιουργήθηκε: ${data.data.code}`);
      setShowCreate(false); setForm(EMPTY_FORM); load();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Η δημιουργία απέτυχε');
    } finally { setSaving(false); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success('Αντιγράφηκε')).catch(() => { /* clipboard access can be denied by the browser; the code is on screen to copy by hand */ });
  };

  return (
    <div>
      <PageHeader
        title="Κωδικοί & Δωρεάν Συνεδρίες"
        subtitle="Έκπτωση: αφαιρείται από την προμήθεια DocNow, ποτέ από την αμοιβή του ιατρού. Δωρεάν: μόνο με ιατρούς που έχουν συμφωνήσει."
        action={<ActionBtn variant="primary" onClick={() => setShowCreate(true)}>+ Νέος κωδικός</ActionBtn>}
      />

      <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>Κωδικός</th><th style={th}>Είδος</th><th style={th}>Ποσό</th>
            <th style={th}>Ιατρός</th><th style={th}>Ασθενής</th><th style={th}>Χρήσεις</th>
            <th style={th}>Λήξη</th><th style={th}>Σημείωση</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td style={td} colSpan={9}>Φόρτωση…</td></tr>}
            {!loading && codes.length === 0 && <tr><td style={td} colSpan={9}>Δεν υπάρχουν κωδικοί ακόμη.</td></tr>}
            {codes.map(row => {
              const exhausted = row.uses_count >= row.max_uses;
              const expired = row.expires_at && new Date(row.expires_at) < new Date();
              return (
                <tr key={row.id} style={{ opacity: row.is_active && !exhausted && !expired ? 1 : 0.55, cursor: 'pointer' }}
                    onClick={() => openRedemptions(row)}>
                  <td style={td}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{row.code}</span>
                    <button onClick={(e) => { e.stopPropagation(); copy(row.code); }}
                            style={{ marginLeft: 6, border: 'none', background: 'none', cursor: 'pointer', fontSize: 12 }} title="Αντιγραφή">📋</button>
                  </td>
                  <td style={td}><Pill text={KIND_EL[row.kind]} color={KIND_COLOR[row.kind]} /></td>
                  <td style={td}>
                    {row.discount_percent != null
                      ? <span style={{ fontWeight: 700 }}>{row.discount_percent}%</span>
                      : euros(row.discount_cents)}
                  </td>
                  <td style={td}>{row.doctor_last ? `${row.doctor_first} ${row.doctor_last}` : 'Όλοι'}</td>
                  <td style={td}>{row.patient_email || 'Όλοι'}</td>
                  <td style={td}>{row.uses_count} / {row.max_uses}{exhausted && <Pill text="Εξαντλήθηκε" color="#DC2626" />}</td>
                  <td style={td}>{row.expires_at ? fmtDate(row.expires_at) : '—'}{expired && <Pill text="Έληξε" color="#DC2626" />}</td>
                  <td style={{ ...td, maxWidth: 220, color: '#6b7280' }}>{row.note || ''}</td>
                  <td style={td} onClick={(e) => e.stopPropagation()}>
                    <ActionBtn onClick={() => toggleActive(row)}>{row.is_active ? 'Απενεργοποίηση' : 'Ενεργοποίηση'}</ActionBtn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Νέος κωδικός">
        <FormField label="Είδος" required>
          <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, doctorId: '' })}>
            <option value="platform">Έκπτωση — την απορροφά η DocNow (έως την προμήθεια)</option>
            <option value="waiver">Δωρεάν — ο ιατρός παραιτείται της αμοιβής</option>
          </Select>
        </FormField>
        <FormField label={form.unit === 'pct' ? 'Έκπτωση (%)' : 'Ποσό (€)'} required>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Input type='number'
                     min={form.unit === 'pct' ? '1' : '0.5'}
                     step={form.unit === 'pct' ? '1' : '0.5'}
                     value={form.unit === 'pct' ? form.discountPercent : form.discountEuros}
                     onChange={(e) => setForm(form.unit === 'pct'
                       ? { ...form, discountPercent: e.target.value }
                       : { ...form, discountEuros: e.target.value })}
                     placeholder={form.unit === 'pct'
                       ? (form.kind === 'waiver' ? 'π.χ. 100 για πλήρως δωρεάν' : 'π.χ. 25')
                       : (form.kind === 'waiver' ? 'π.χ. 20 για πλήρως δωρεάν' : 'π.χ. 5')} />
            </div>
            <div style={{ width: 92 }}>
              <Select value={form.unit}
                      onChange={(e) => setForm({ ...form, unit: e.target.value, discountEuros: '', discountPercent: '' })}>
                <option value='eur'>€</option>
                <option value='pct'>%</option>
              </Select>
            </div>
          </div>
        </FormField>
        {impact.length > 0 && (
          <div style={{ margin: '-4px 0 14px', padding: '10px 12px', background: '#F8FAFC',
                        border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12.5 }}>
            <div style={{ fontWeight: 700, color: '#334155', marginBottom: 6 }}>Τι θα ισχύσει στην πράξη</div>
            {impact.map(r => (
              <div key={r.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '3px 0' }}>
                <span style={{ color: '#475569' }}>{r.name} · {euros(r.fee)}</span>
                <span style={{ color: r.clipped ? '#B45309' : '#0F172A', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  −{euros(r.actual)} ({r.share.toFixed(1).replace('.', ',')}%)
                  {r.clipped && ' ⚠ αντί ' + euros(r.requested)}
                </span>
              </div>
            ))}
            {impact.some(r => r.clipped) && (
              <div style={{ marginTop: 6, color: '#B45309' }}>
                ⚠ Περιορίζεται στην προμήθεια της DocNow — ένας κωδικός «Έκπτωση» δεν κοστίζει ποτέ χρήματα στον ιατρό.
              </div>
            )}
          </div>
        )}
        {form.kind === 'waiver' ? (
          <FormField label="Ιατρός" required>
            <Select value={form.doctorId} onChange={(e) => setForm({ ...form, doctorId: e.target.value })}>
              <option value="">— επιλέξτε —</option>
              {waiverDoctors.map(d => <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>)}
            </Select>
          </FormField>
        ) : (
          <FormField label='Ιατρός (προαιρετικό — περιορίζει τον κωδικό σε έναν ιατρό)'>
            <Select value={form.doctorId} onChange={(e) => setForm({ ...form, doctorId: e.target.value })}>
              <option value=''>— όλοι οι ιατροί —</option>
              {allDoctors.map(d => (
                <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
              ))}
            </Select>
          </FormField>
        )}
        <FormField label="Email ασθενούς (προαιρετικό — μόνο για αυτόν τον ασθενή)">
          <Input value={form.patientEmail} onChange={(e) => setForm({ ...form, patientEmail: e.target.value })} placeholder="κενό = οποιοσδήποτε" />
        </FormField>
        <FormField label="Μέγιστες χρήσεις" required>
          <Input type="number" min="1" step="1" value={form.maxUses} onChange={(e) => setForm({ ...form, maxUses: e.target.value })} />
        </FormField>
        <FormField label="Λήξη (προαιρετικό)">
          <Input type="datetime-local" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
        </FormField>
        <FormField label="Κωδικός (προαιρετικό — κενό = αυτόματος DN-XXXXXX)">
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="π.χ. ΚΑΛΩΣΗΡΘΕΣ" />
        </FormField>
        <FormField label="Σημείωση (εσωτερική)">
          <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="π.χ. Goodwill — περιστατικό 9/9" />
        </FormField>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <ActionBtn onClick={() => setShowCreate(false)}>Άκυρο</ActionBtn>
          <ActionBtn variant="primary" onClick={submit} disabled={saving}>{saving ? 'Αποθήκευση…' : 'Δημιουργία'}</ActionBtn>
        </div>
      </Modal>

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected ? `Χρήσεις: ${selected.code}` : ''} size="lg">
        {redLoading && <div>Φόρτωση…</div>}
        {!redLoading && redemptions.length === 0 && <div style={{ color: '#6b7280' }}>Δεν έχει χρησιμοποιηθεί ακόμη.</div>}
        {!redLoading && redemptions.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}>Πότε</th><th style={th}>Ασθενής</th><th style={th}>Ιατρός</th><th style={th}>Ραντεβού</th><th style={th}>Έκπτωση</th><th style={th}>Πληρώθηκε</th><th style={th}>Κατάσταση</th></tr></thead>
            <tbody>
              {redemptions.map(r => (
                <tr key={r.payment_id}>
                  <td style={td}>{fmtDate(r.created_at)}</td>
                  <td style={td}>{r.patient_name}<br /><span style={{ color: '#6b7280', fontSize: 12 }}>{r.patient_email}</span></td>
                  <td style={td}>{r.doctor_name}</td>
                  <td style={td}>{r.appointment_date ? `${r.appointment_date.slice(0, 10)} ${String(r.start_time).slice(0, 5)}` : '—'}</td>
                  <td style={td}>{euros(r.discount_cents)}{r.clipped && <span style={{ color: '#B45309', whiteSpace: 'nowrap' }}>{' ⚠ αντί ' + euros(r.requested_cents)}</span>}</td>
                  <td style={td}>{Number(r.amount_euros).toFixed(2)} €</td>
                  <td style={td}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    </div>
  );
}
